// 053 — Referencia, Derivación, Contrarreferencia y Referencia Inversa (Fase 5,
// Tanda 4, ver PLAN.md). Transcripción del PDF oficial (página 187, footer
// `SNS-MSP/HCU-form.053/2021`, instructivo "7 Bloques"): una hoja con DOS
// mitades independientes —
// 1. Referencia / Derivación (A-G): la emite el establecimiento de origen; G
//    ("evaluación") la llena el que recibe.
// 2. Contrarreferencia / Referencia inversa (A-H): la emite el
//    establecimiento que devuelve al paciente.
// Cada mitad se guarda por separado (una NO obliga a la otra), mismo criterio
// que el stub anterior y que 007. Los datos del paciente (nombre, sexo, edad) viven en `Patient` y el encabezado MSP
// los muestra; el teléfono y el lugar de residencia actual (provincia, cantón, parroquia) también viven en `Patient`,
// pero el encabezado NO los imprime, y el bloque A del papel (instructivo, pág. 189) los pide: por eso son campos del
// bloque A (`CAMPOS_PACIENTE_053`), precargados desde el `Patient` (`valoresPacienteDesdePatient053`), editables e
// impresos en el `Par[]` de la mitad Referencia/Derivación. El tipo (referencia/derivación, contrarreferencia/referencia
// inversa) NO arranca preseleccionado: el papel obliga a marcar uno de los casilleros.
import type { CodeableConcept, Organization, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { telefonosDesdePatient } from '../../paciente/filiacionDatos';
import { diagnosticoConMarcaVacio, textoDiagnosticoConMarca, type DiagnosticoConMarca } from './MarcaDiagnostico';
import { MARCAS_PRE_DEF, paresDeCampos, valoresVacios, type CampoSimple, type Valores } from './camposSimplesDatos';
import type { Par } from './index';
import type { Traducir } from './tecnicaAnestesicaDatos';

export const NUM_DIAGNOSTICOS_053 = 6;

export const OPCIONES_TIPO_REFERENCIA_053 = [
  { valor: 'referencia', etiqueta: 'Referencia' },
  { valor: 'derivacion', etiqueta: 'Derivación' },
] as const;

export const OPCIONES_TIPO_CONTRARREFERENCIA_053 = [
  { valor: 'contrarreferencia', etiqueta: 'Contrarreferencia' },
  { valor: 'referenciaInversa', etiqueta: 'Referencia inversa' },
] as const;

/** Los 9 motivos impresos, en su orden. */
export const MOTIVOS_053: readonly { readonly clave: string; readonly etiqueta: string }[] = [
  { clave: 'accesibilidadGeografica', etiqueta: 'Accesibilidad geográfica' },
  { clave: 'faltaEspacioFisico', etiqueta: 'Falta de espacio físico' },
  { clave: 'faltaEquipamiento', etiqueta: 'Falta de equipamiento' },
  { clave: 'equiposMalEstado', etiqueta: 'Equipos en mal estado' },
  { clave: 'problemasInfraestructura', etiqueta: 'Problemas de infraestructura' },
  { clave: 'problemasAbastecimiento', etiqueta: 'Problemas de abastecimiento' },
  { clave: 'insuficienciaProfesionales', etiqueta: 'Insuficiencia de profesionales' },
  { clave: 'inadecuadaCapacidadResolutiva', etiqueta: 'Inadecuada capacidad resolutiva' },
  { clave: 'ausenciaPrestacionCartera', etiqueta: 'Ausencia de la prestación en la cartera de servicios' },
];

export const CAMPOS_ORIGEN_REFERENCIA_053: readonly CampoSimple[] = [{ clave: 'tipologia', etiqueta: 'Tipología del establecimiento de origen' }];

/** Bloque A (solo mitad Referencia/Derivación): "No. teléfono (celular o convencional)" y "Lugar de residencia actual". */
export const CAMPOS_PACIENTE_053: readonly CampoSimple[] = [
  { clave: 'telefono', etiqueta: 'No. teléfono (celular o convencional)' },
  { clave: 'provincia', etiqueta: 'Lugar de residencia actual — Provincia' },
  { clave: 'canton', etiqueta: 'Lugar de residencia actual — Cantón' },
  { clave: 'parroquia', etiqueta: 'Lugar de residencia actual — Parroquia' },
];

/** Precarga del bloque A desde el `Patient`: celular si hay, si no el convencional; provincia/cantón/parroquia = `address.state/district/city`. */
export function valoresPacienteDesdePatient053(patient: Patient | undefined): Valores {
  const vacio = valoresVacios(CAMPOS_PACIENTE_053);
  if (!patient) return vacio;
  const tel = telefonosDesdePatient(patient);
  const dir = patient.address?.[0];
  return { ...vacio, telefono: tel.celular || tel.fijo, provincia: dir?.state ?? '', canton: dir?.district ?? '', parroquia: dir?.city ?? '' };
}

export const CAMPOS_DESTINO_REFERENCIA_053: readonly CampoSimple[] = [
  { clave: 'institucion', etiqueta: 'Institución del sistema' },
  { clave: 'establecimiento', etiqueta: 'Establecimiento de salud' },
  { clave: 'servicio', etiqueta: 'Servicio' },
  { clave: 'especialidad', etiqueta: 'Especialidad' },
];

export const CAMPOS_ORIGEN_CONTRARREFERENCIA_053: readonly CampoSimple[] = [
  { clave: 'institucion', etiqueta: 'Institución del sistema' },
  { clave: 'unicodigo', etiqueta: 'Unicódigo' },
  { clave: 'establecimiento', etiqueta: 'Establecimiento de salud' },
  { clave: 'tipologia', etiqueta: 'Tipología' },
  { clave: 'numeroHcu', etiqueta: 'Número de historia clínica única' },
  { clave: 'numeroArchivo', etiqueta: 'Número de archivo' },
];

export const CAMPOS_DESTINO_CONTRARREFERENCIA_053: readonly CampoSimple[] = [
  { clave: 'institucion', etiqueta: 'Institución del sistema' },
  { clave: 'establecimiento', etiqueta: 'Establecimiento de salud' },
  { clave: 'distrito', etiqueta: 'Distrito' },
  { clave: 'fecha', etiqueta: 'Fecha', tipo: 'fecha' },
];

// Bloques F (referencia/derivación) y H (contrarreferencia/referencia inversa): "DATOS DEL PROFESIONAL RESPONSABLE" (fecha, hora, nombres,
// documento, firma, sello). Ya NO se teclean: en ambas mitades es el profesional que emite y firma este documento, y su nombre, cédula y
// fecha los arma el servidor al firmar (`CODIGOS_CON_CEDULA_PROFESIONAL`, incluye '053'). Un estado viejo con `responsable` se lee sin error
// y no se vuelve a imprimir. Lo que sí es de OTRO establecimiento (destino, G evaluación) sigue tecleado.

export type ValorSiNo053 = '' | 'si' | 'no';

export interface EstadoReferencia053 {
  /** Vacío hasta que se elija: el papel obliga a marcar Referencia o Derivación. */
  readonly tipo: string;
  /** Bloque A: teléfono y residencia del paciente (precargados del `Patient`, editables). No cuenta como "datos" de la mitad. */
  readonly paciente: Valores;
  readonly motivos: Readonly<Record<string, boolean>>;
  readonly origen: Valores;
  readonly destino: Valores;
  readonly resumenCuadroClinico: string;
  readonly hallazgos: string;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  /** G — la llena el establecimiento que recibe, no el que emite. */
  readonly referenciaJustificada: ValorSiNo053;
  readonly derivacionJustificada: ValorSiNo053;
}

export interface EstadoContrarreferencia053 {
  readonly tipo: string;
  readonly origen: Valores;
  readonly destino: Valores;
  readonly resumenCuadroClinico: string;
  readonly hallazgos: string;
  readonly tratamientosRealizados: string;
  readonly diagnosticos: readonly DiagnosticoConMarca[];
  readonly tratamientoRecomendado: string;
}

export interface EstadoBitacora053 {
  readonly referencia: EstadoReferencia053;
  readonly contrarreferencia: EstadoContrarreferencia053;
}

function diagnosticosVacios(): DiagnosticoConMarca[] {
  return Array.from({ length: NUM_DIAGNOSTICOS_053 }, () => diagnosticoConMarcaVacio());
}

/** `tipo` arranca vacío en ambas mitades: el papel tiene un casillero a marcar, no un valor por defecto — hay que elegirlo antes de guardar. */
export function estadoInicialBitacora053(): EstadoBitacora053 {
  return {
    referencia: {
      tipo: '',
      paciente: valoresVacios(CAMPOS_PACIENTE_053),
      motivos: Object.fromEntries(MOTIVOS_053.map((m) => [m.clave, false])),
      origen: valoresVacios(CAMPOS_ORIGEN_REFERENCIA_053),
      destino: valoresVacios(CAMPOS_DESTINO_REFERENCIA_053),
      resumenCuadroClinico: '',
      hallazgos: '',
      diagnosticos: diagnosticosVacios(),
      referenciaJustificada: '',
      derivacionJustificada: '',
    },
    contrarreferencia: {
      tipo: '',
      origen: valoresVacios(CAMPOS_ORIGEN_CONTRARREFERENCIA_053),
      destino: valoresVacios(CAMPOS_DESTINO_CONTRARREFERENCIA_053),
      resumenCuadroClinico: '',
      hallazgos: '',
      tratamientosRealizados: '',
      diagnosticos: diagnosticosVacios(),
      tratamientoRecomendado: '',
    },
  };
}

function hayTexto(...textos: readonly string[]): boolean {
  return textos.some((t) => t.trim() !== '');
}

function hayValores(v: Valores): boolean {
  return Object.values(v).some((x) => x.trim() !== '');
}

function hayDiagnosticos(d: readonly DiagnosticoConMarca[]): boolean {
  return d.some((x) => x.diagnostico.texto.trim() !== '');
}

/** La mitad de Referencia/Derivación se guarda solo si hay establecimiento de destino (es lo que la vuelve un `ServiceRequest` con sentido). */
export function referenciaSeGuarda(e: EstadoReferencia053): boolean {
  return (e.destino.establecimiento ?? '').trim() !== '';
}

/** Hay algo cargado en la mitad de Referencia/Derivación aunque falte el destino — para avisar en vez de descartar en silencio. */
export function referenciaTieneDatos(e: EstadoReferencia053): boolean {
  return (
    Object.values(e.motivos).some(Boolean) ||
    hayValores(e.origen) ||
    hayValores(e.destino) ||
    hayTexto(e.resumenCuadroClinico, e.hallazgos) ||
    hayDiagnosticos(e.diagnosticos) ||
    e.referenciaJustificada !== '' ||
    e.derivacionJustificada !== ''
  );
}

/**
 * Referencia y contrarreferencia las emiten establecimientos distintos y el documento lleva UN solo firmante (el de la sesión): si ambas
 * mitades traen datos en el mismo guardado, se pide guardar cada una por separado (cada una con su propio firmante).
 */
export function ambasMitadesConDatos053(ref: EstadoReferencia053, contra: EstadoContrarreferencia053): boolean {
  return referenciaTieneDatos(ref) && contrarreferenciaTieneDatos(contra);
}

export function contrarreferenciaTieneDatos(e: EstadoContrarreferencia053): boolean {
  return hayValores(e.origen) || hayValores(e.destino) || hayTexto(e.resumenCuadroClinico, e.hallazgos, e.tratamientosRealizados, e.tratamientoRecomendado) || hayDiagnosticos(e.diagnosticos);
}

/** Lo que falta elegir para poder guardar: el tipo de cada mitad que se va a guardar (el papel obliga a marcarlo). */
export function faltantesObligatorios053(ref: EstadoReferencia053, contra: EstadoContrarreferencia053): string[] {
  const faltan: string[] = [];
  if (referenciaSeGuarda(ref) && !etiquetaTipoReferencia053(ref.tipo)) faltan.push('Tipo (Referencia o Derivación)');
  if (contrarreferenciaTieneDatos(contra) && !etiquetaTipoContrarreferencia053(contra.tipo)) faltan.push('Tipo (Contrarreferencia o Referencia inversa)');
  return faltan;
}

export function etiquetaTipoReferencia053(tipo: string): string {
  return OPCIONES_TIPO_REFERENCIA_053.find((o) => o.valor === tipo)?.etiqueta ?? '';
}

export function etiquetaTipoContrarreferencia053(tipo: string): string {
  return OPCIONES_TIPO_CONTRARREFERENCIA_053.find((o) => o.valor === tipo)?.etiqueta ?? '';
}

export function motivosSeleccionados053(motivos: Readonly<Record<string, boolean>>): string[] {
  return MOTIVOS_053.filter((m) => motivos[m.clave]).map((m) => m.etiqueta);
}

function etiquetaSiNo(v: ValorSiNo053): string {
  return v === 'si' ? 'Sí' : v === 'no' ? 'No' : '';
}

function paresDeDiagnosticos(t: Traducir, clave: string, diagnosticos: readonly DiagnosticoConMarca[]): Par[] {
  return diagnosticos.map((d, i) => ({
    pregunta: `${t(clave)} — ${i + 1}`,
    respuesta: textoDiagnosticoConMarca(d, MARCAS_PRE_DEF),
  }));
}

/** `Par[]` de la mitad Referencia/Derivación — vacía (sin pares) si esa mitad no se guarda. */
export function paresDeReferencia053(t: Traducir, e: EstadoReferencia053): Par[] {
  const pares: Par[] = [
    { pregunta: t('formularios.f053.tipoReferencia'), respuesta: etiquetaTipoReferencia053(e.tipo) },
    ...paresDeCampos(CAMPOS_PACIENTE_053, e.paciente),
    { pregunta: t('formularios.f053.motivoTitulo'), respuesta: motivosSeleccionados053(e.motivos).join('; ') },
    ...paresDeCampos(CAMPOS_ORIGEN_REFERENCIA_053, e.origen),
    ...paresDeCampos(CAMPOS_DESTINO_REFERENCIA_053, e.destino, `${t('formularios.f053.destinoTitulo')} — `),
    { pregunta: t('formularios.f053.resumenTitulo'), respuesta: e.resumenCuadroClinico.trim() },
    { pregunta: t('formularios.f053.hallazgosTitulo'), respuesta: e.hallazgos.trim() },
    ...paresDeDiagnosticos(t, 'formularios.f053.diagnosticoTitulo', e.diagnosticos),
    { pregunta: t('formularios.f053.referenciaJustificada'), respuesta: etiquetaSiNo(e.referenciaJustificada) },
    { pregunta: t('formularios.f053.derivacionJustificada'), respuesta: etiquetaSiNo(e.derivacionJustificada) },
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}

/** `Par[]` de la mitad Contrarreferencia/Referencia inversa. */
export function paresDeContrarreferencia053(t: Traducir, e: EstadoContrarreferencia053): Par[] {
  const pares: Par[] = [
    { pregunta: t('formularios.f053.tipoContrarreferencia'), respuesta: etiquetaTipoContrarreferencia053(e.tipo) },
    ...paresDeCampos(CAMPOS_ORIGEN_CONTRARREFERENCIA_053, e.origen, `${t('formularios.f053.origenContraTitulo')} — `),
    ...paresDeCampos(CAMPOS_DESTINO_CONTRARREFERENCIA_053, e.destino, `${t('formularios.f053.destinoContraTitulo')} — `),
    { pregunta: `${t('formularios.f053.contraTitulo')} — ${t('formularios.f053.resumenTitulo')}`, respuesta: e.resumenCuadroClinico.trim() },
    { pregunta: `${t('formularios.f053.contraTitulo')} — ${t('formularios.f053.hallazgosTitulo')}`, respuesta: e.hallazgos.trim() },
    { pregunta: t('formularios.f053.tratamientosRealizadosTitulo'), respuesta: e.tratamientosRealizados.trim() },
    ...paresDeDiagnosticos(t, 'formularios.f053.diagnosticoContraTitulo', e.diagnosticos),
    { pregunta: t('formularios.f053.tratamientoRecomendadoTitulo'), respuesta: e.tratamientoRecomendado.trim() },
  ];
  return pares.filter((p) => p.respuesta.trim() !== '');
}

// ---------- FHIR: datos del bloque B/origen/responsable que antes solo viajaban en el `Par[]` ----------
// El `ServiceRequest` guardaba solo el establecimiento de destino (`performer`) y la `Communication` solo el establecimiento (`recipient`).
// Lo demás del papel (institución/servicio/especialidad destino, tipología de origen, evaluaciones, fechas, distrito, responsable) se completa
// con campos estándar, SIN cambiar lo que ya se guardaba: `performerType`, `requester`, `sender`, y líneas extra de `note` / `payload` AL FINAL.

/** "Título — Etiqueta: valor; Etiqueta: valor" con los campos que traen texto; vacío si ninguno. */
function lineaCampos(titulo: string, campos: readonly CampoSimple[], valores: Valores): string {
  const partes = campos.filter((c) => (valores[c.clave] ?? '').trim() !== '').map((c) => `${c.etiqueta}: ${(valores[c.clave] ?? '').trim()}`);
  return partes.length > 0 ? `${titulo} — ${partes.join('; ')}` : '';
}

/** `ServiceRequest.performerType`: la especialidad (o el servicio) a la que se refiere/deriva. */
export function performerTypeReferencia053(e: EstadoReferencia053): CodeableConcept | undefined {
  const texto = (e.destino.especialidad ?? '').trim() || (e.destino.servicio ?? '').trim();
  return texto ? { text: texto } : undefined;
}

/** Lo mínimo que se usa del perfil de la sesión (`medplum.getProfile()`): el Practitioner que emite. */
export interface PerfilSesion053 {
  readonly resourceType: string;
  readonly id?: string;
  readonly name?: readonly { readonly given?: readonly string[]; readonly family?: string; readonly text?: string }[];
}

function nombrePerfil053(p: PerfilSesion053): string {
  const n = p.name?.[0];
  return n?.text?.trim() || [...(n?.given ?? []), n?.family ?? ''].map((x) => x.trim()).filter(Boolean).join(' ');
}

/**
 * `ServiceRequest.requester`: el profesional que emite = el Practitioner de la sesión (`getProfile()`, como 006 y 117), con su `reference`
 * y su nombre. Ya no hay bloque F tecleado que lo complemente ni lo sustituya; sin perfil de Practitioner queda vacío.
 */
export function requesterResponsable053(perfil?: PerfilSesion053 | null): Reference<Practitioner> | undefined {
  if (perfil?.resourceType !== 'Practitioner' || !perfil.id) return undefined;
  const nombre = nombrePerfil053(perfil);
  return { reference: `Practitioner/${perfil.id}`, ...(nombre ? { display: nombre } : {}) };
}

/** Líneas extra de `ServiceRequest.note` de la mitad Referencia/Derivación (se agregan después de las que ya se guardaban). */
export function notasExtraReferencia053(e: EstadoReferencia053): string[] {
  const evaluacion = [e.referenciaJustificada ? `Referencia justificada: ${etiquetaSiNo(e.referenciaJustificada)}` : '', e.derivacionJustificada ? `Derivación justificada: ${etiquetaSiNo(e.derivacionJustificada)}` : ''].filter(Boolean);
  return [
    lineaCampos('Origen', CAMPOS_ORIGEN_REFERENCIA_053, e.origen),
    lineaCampos('Destino', CAMPOS_DESTINO_REFERENCIA_053, e.destino),
    evaluacion.length > 0 ? `Evaluación de la referencia/derivación — ${evaluacion.join('; ')}` : '',
  ].filter(Boolean);
}

/** `Communication.sender`: el establecimiento que contrarrefiere (bloque A de la mitad de contrarreferencia). */
export function senderContrarreferencia053(e: EstadoContrarreferencia053): Reference<Organization> | undefined {
  const display = (e.origen.establecimiento ?? '').trim();
  const unicodigo = (e.origen.unicodigo ?? '').trim();
  if (!display && !unicodigo) return undefined;
  return { ...(display ? { display } : {}), ...(unicodigo ? { identifier: { system: 'http://logiacare.ec/fhir/unicodigo', value: unicodigo } } : {}) };
}

/** Entradas extra de `Communication.payload` (origen, destino con distrito y fecha), después de las que ya se guardaban. */
export function payloadExtraContrarreferencia053(e: EstadoContrarreferencia053): string[] {
  return [
    lineaCampos('Origen', CAMPOS_ORIGEN_CONTRARREFERENCIA_053, e.origen),
    lineaCampos('Destino', CAMPOS_DESTINO_CONTRARREFERENCIA_053, e.destino),
  ].filter(Boolean);
}
